import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { useTeamMutations } from "@/hooks/use-team-mutations";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";

export function RenameTeamDialog({
  team,
  open,
  onOpenChange,
}: {
  team: { id: string; name: string } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [name, setName] = useState(team?.name ?? "");
  const { updateTeam } = useTeamMutations();

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!team) return;
    try {
      await updateTeam.mutateAsync({ teamId: team.id, name });
      toast.success("Team renamed");
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to rename team");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename team</DialogTitle>
          <DialogDescription>Pick a new name for this team.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="rename-team">Name</Label>
            <Input
              id="rename-team"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <DialogFooter>
            <Button
              type="submit"
              disabled={updateTeam.isPending || !name.trim() || name === team?.name}
            >
              {updateTeam.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
